import {
  CompositeNavigationProp,
  useNavigation,
  useRoute,
} from '@react-navigation/native';
import { styled, SxProp } from 'dripsy';
import { observer } from 'mobx-react-lite';
import React, { useCallback, Fragment } from 'react';
import { View } from 'react-native';
import {
  EdgeInsets,
  useSafeAreaInsets,
} from 'react-native-safe-area-context';
import { useModal } from 'react-native-modalfy';

import {
  MainNavigatorNavigationPropType,
  MainNavigatorRoutePropType,
  PersonalizationNavigatorNavigationPropType,
  PersonalizationNavigatorRoutePropType,
  screens,
} from '@navigation';
import {
  EmptyResultTypeEnum,
  IPostItemProps,
  PostList,
  Separator,
  Line,
} from '@components';
import { useViewerModel } from '@models/Viewer/ViewerModel';
import { ToastService } from '@services';
import { IS_WEB, parseApiError } from '@utils';
import { useAsyncInit } from '@hooks';
import { Post } from '@models/Posts/PostModel';
import { modals, ModalStackParamsType } from '@modals';
import { User } from '@models/Viewer/UserModel';
import { UserProfileHeader } from './components/UserProfileHeader';

const Container = styled(View)({
  flex: 1,
  bg: '$background0',
});

function contentContainerStyle(
  props: EdgeInsets,
  length?: number,
): SxProp {
  const styles: SxProp = { flexGrow: 1, pb: props.bottom };

  if (length && length > 0) {
    styles.pl = ['$6', '5%'];
    styles.pr = ['$6', '30%'];
  } else {
    styles.px = ['$6', '5%'];
  }

  return styles;
}

type RoutePropType =
  | PersonalizationNavigatorRoutePropType<'UserProfile'>
  | MainNavigatorRoutePropType<'UserProfile'>;

type NavigationPropType = CompositeNavigationProp<
  PersonalizationNavigatorNavigationPropType<'UserProfile'>,
  MainNavigatorNavigationPropType<'UserProfile'>
>;

export const UserProfileScreen = observer(() => {
  const route = useRoute<RoutePropType>();
  const nav = useNavigation<NavigationPropType>();
  const insets = useSafeAreaInsets();
  const viewer = useViewerModel();
  const modal = useModal<ModalStackParamsType>();
  const user = viewer.getUserById(route.params.id);

  useAsyncInit(() => fetch(), [user]);

  async function fetch() {
    try {
      await user?.posts.fetch.run();
    } catch (error) {
      ToastService.showError(parseApiError(error).message);
    }
  }

  async function fetchMore() {
    try {
      await user?.posts.fetchMore.run();
    } catch (error) {
      ToastService.showError(parseApiError(error).message);
    }
  }

  async function refresh() {
    try {
      await Promise.all([
        viewer.fetchUserById.run(route.params.id),
        fetch(),
      ]);
    } catch (error) {
      ToastService.showError(parseApiError(error).message);
    }
  }

  const postItemAction = useCallback<IPostItemProps['onPress']>(
    (item: Post, imageIndex: number) => {
      // We have here only post of one type - UserStatus
      if (IS_WEB) {
        modal.openModal(modals.PostViewModal, {
          item,
          imageIndex,
          onNavigateToUser,
        });
      } else {
        nav.navigate(screens.Gallery, {
          images: item.itemData.images,
          imageIndex,
        });
      }
    },
    [],
  );

  const UserProfileHeaderComponent = useCallback(
    () => (
      <Fragment>
        <UserProfileHeader />
        <Line />
        <Separator size={['$9', '$10']} />
      </Fragment>
    ),
    [],
  );

  const onNavigateToUser = useCallback((userParam: User) => {
    nav.push(screens.UserProfile, { id: userParam.id });
  }, []);

  const isLoading =
    user?.posts.fetch.inProgress && user?.posts.isEmpty;

  let emptyResultType: EmptyResultTypeEnum =
    EmptyResultTypeEnum.Error;

  if (user?.posts.fetch.isError) {
    emptyResultType = EmptyResultTypeEnum.Error;
  } else if (user?.posts.isEmpty) {
    emptyResultType = EmptyResultTypeEnum.NoUserPosts;
  }

  return (
    <Container>
      <PostList
        ListHeaderComponent={UserProfileHeaderComponent}
        contentContainerSx={contentContainerStyle(
          insets,
          user?.posts.asArray.length,
        )}
        onPress={postItemAction}
        isLoading={isLoading}
        isLoadingMore={user?.posts.fetchMore.inProgress}
        onRefresh={refresh}
        onEndReached={fetchMore}
        data={user?.posts.asArray}
        emptyResultType={emptyResultType}
      />
    </Container>
  );
});
